/**
 * Company Alert Settings Page
 *
 * Unified alert configuration using tabs for each tool.
 */

import { useState, useEffect, useRef } from 'react';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAuth } from '@/contexts/AuthContext';
import { usePermission } from '@/hooks/usePermissions';
import { AccessDenied } from '@/components/AccessDenied';
import { AppShell3 } from '@/components/app-shell-3';
import { ToolHeader } from '@/components/ToolHeader';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { motion, AnimatePresence } from 'framer-motion';
import { PageTransition } from '@/components/PageTransition';
import {
  Bell,
  Save,
  Calendar,
  CheckSquare,
  MapPin,
  Award,
  Users,
  Grid3X3,
  CalendarDays,
  MessageSquare,
  FileText,
  Clock,
  Plus,
  X,
  AlertTriangle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { ALERT_DEFINITIONS } from '@/config/alertDefinitions';
import { GLOBAL_TOOLS } from '@/config/tools';
import { useNavigate } from 'react-router-dom';

const ICON_MAP = {
  Calendar,
  CheckSquare,
  MapPin,
  Award,
  Users,
  Grid3X3,
  'Grid3x3': Grid3X3,
  CalendarDays,
  MessageSquare,
  FileText
};

export function CompanyAlertSettingsPage() {
  const navigate = useNavigate();
  const { userProfile, company } = useAuth();
  const canManageSettings = usePermission('settings.manage');
  usePageTitle('Company Alerts');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState(null);

  // Tab scroll state
  const tabsContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const [adminSettings, setAdminSettings] = useState({});
  const [managerSettings, setManagerSettings] = useState({});
  const [thresholdConfig, setThresholdConfig] = useState({
    critical_shortage_threshold: 3,
    expiry_warning_days: [30, 14, 7]
  });
  const [newThresholdDays, setNewThresholdDays] = useState('');

  const toolEntries = Object.entries(ALERT_DEFINITIONS);

  // Set default tab
  useEffect(() => {
    if (!activeTab && toolEntries.length > 0) {
      setActiveTab(toolEntries[0][0]);
    }
  }, [toolEntries, activeTab]);

  // Check scroll indicators
  const updateScrollIndicators = () => {
    const container = tabsContainerRef.current;
    if (container) {
      setCanScrollLeft(container.scrollLeft > 0);
      setCanScrollRight(container.scrollLeft < container.scrollWidth - container.clientWidth - 1);
    }
  };

  useEffect(() => {
    updateScrollIndicators();
    const container = tabsContainerRef.current;
    if (container) {
      container.addEventListener('scroll', updateScrollIndicators);
      window.addEventListener('resize', updateScrollIndicators);
      return () => {
        container.removeEventListener('scroll', updateScrollIndicators);
        window.removeEventListener('resize', updateScrollIndicators);
      };
    }
  }, []);

  const scrollTabs = (direction) => {
    const container = tabsContainerRef.current;
    if (container) {
      const scrollAmount = 200;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    if (!canManageSettings) return;
    loadAlertSettings();
  }, [company?.id, canManageSettings]);

  const loadAlertSettings = async () => {
    if (!company?.id) return;

    try {
      setLoading(true);

      const [alertSettingsResult, globalConfigResult] = await Promise.all([
        supabase.from('company_alert_settings').select('tool_id, role, settings').eq('company_id', company.id),
        supabase.from('company_alert_config').select('*').eq('company_id', company.id).single()
      ]);

      if (alertSettingsResult.error) throw alertSettingsResult.error;

      const adminSettingsMap = {};
      const managerSettingsMap = {};

      Object.entries(ALERT_DEFINITIONS).forEach(([toolId, toolDef]) => {
        adminSettingsMap[toolId] = {};
        managerSettingsMap[toolId] = {};
        Object.entries(toolDef.alerts).forEach(([alertId, alertDef]) => {
          adminSettingsMap[toolId][alertId] = { ...alertDef.defaults.Admin };
          managerSettingsMap[toolId][alertId] = { ...alertDef.defaults.Manager };
        });
      });

      if (alertSettingsResult.data) {
        alertSettingsResult.data.forEach((setting) => {
          if (setting.role === 'Admin') adminSettingsMap[setting.tool_id] = setting.settings;
          else if (setting.role === 'Manager') managerSettingsMap[setting.tool_id] = setting.settings;
        });
      }

      setAdminSettings(adminSettingsMap);
      setManagerSettings(managerSettingsMap);

      if (globalConfigResult.data) {
        setThresholdConfig({
          critical_shortage_threshold: globalConfigResult.data.critical_shortage_threshold ?? 3,
          expiry_warning_days: globalConfigResult.data.expiry_warning_days ?? [30, 14, 7]
        });
      }

      setHasChanges(false);
    } catch (error) {
      console.error('Error loading alert settings:', error);
      toast.error('Failed to load alert settings');
    } finally {
      setLoading(false);
    }
  };

  const handleAlertChange = (role, toolId, alertId, channel, value) => {
    const setter = role === 'Admin' ? setAdminSettings : setManagerSettings;
    setter(prev => ({
      ...prev,
      [toolId]: {
        ...prev[toolId],
        [alertId]: { ...(prev[toolId]?.[alertId] || {}), [channel]: value }
      }
    }));
    setHasChanges(true);
  };

  const handleAddExpiryThreshold = () => {
    const days = parseInt(newThresholdDays);
    if (isNaN(days) || days < 1 || days > 365) {
      toast.error('Enter a valid number between 1 and 365');
      return;
    }
    if (thresholdConfig.expiry_warning_days.includes(days)) {
      toast.error('This threshold already exists');
      return;
    }
    const newDays = [...thresholdConfig.expiry_warning_days, days].sort((a, b) => b - a);
    setThresholdConfig(prev => ({ ...prev, expiry_warning_days: newDays }));
    setNewThresholdDays('');
    setHasChanges(true);
  };

  const handleRemoveExpiryThreshold = (days) => {
    setThresholdConfig(prev => ({
      ...prev,
      expiry_warning_days: prev.expiry_warning_days.filter(d => d !== days)
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!company?.id || !userProfile?.id) return;

    setSaving(true);
    try {
      const records = [];
      Object.entries(adminSettings).forEach(([toolId, settings]) => {
        records.push({ company_id: company.id, tool_id: toolId, role: 'Admin', settings, configured_by: userProfile.id });
      });
      Object.entries(managerSettings).forEach(([toolId, settings]) => {
        records.push({ company_id: company.id, tool_id: toolId, role: 'Manager', settings, configured_by: userProfile.id });
      });

      const [alertResult, configResult] = await Promise.all([
        supabase.from('company_alert_settings').upsert(records, { onConflict: 'company_id,tool_id,role' }),
        supabase.from('company_alert_config').upsert({
          company_id: company.id,
          critical_shortage_threshold: thresholdConfig.critical_shortage_threshold,
          expiry_warning_days: thresholdConfig.expiry_warning_days,
          configured_by: userProfile.id,
          updated_date: new Date().toISOString()
        }, { onConflict: 'company_id' })
      ]);

      if (alertResult.error) throw alertResult.error;
      if (configResult.error) throw configResult.error;

      toast.success('Alert settings saved');
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!canManageSettings) {
    return (
      <AppShell3>
        <AccessDenied message="You don't have permission to manage alert settings." backTo="/company-settings" backLabel="Company Settings" />
      </AppShell3>
    );
  }

  if (loading) {
    return (
      <AppShell3>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="animate-pulse text-gray-500">Loading alert settings...</div>
        </div>
      </AppShell3>
    );
  }

  // Get active tool data
  const activeToolDef = activeTab ? ALERT_DEFINITIONS[activeTab] : null;
  const ActiveToolIcon = activeToolDef ? ICON_MAP[activeToolDef.icon] : null;
  const showCheckinThresholds = activeTab === 'checkins';
  const showQualificationThresholds = activeTab === 'qualifications';

  return (
    <AppShell3>
      <PageTransition>
        <ToolHeader
          spaceName="Company"
          toolName="Alert Settings"
          currentTool="alerts"
          spaceTools={GLOBAL_TOOLS}
          isGlobalView={true}
          parentBreadcrumb={{
            name: 'Company Settings',
            onClick: () => navigate('/company-settings')
          }}
          actions={
            hasChanges && (
              <Button onClick={handleSave} disabled={saving} size="sm">
                {saving ? (
                  <>
                    <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            )
          }
        />

        <div className="min-h-screen bg-gray-50">
          {/* Underline Tabs with Scroll */}
          <div className="bg-white border-b">
            <div className="max-w-7xl mx-auto relative">
              {/* Left scroll indicator */}
              {canScrollLeft && (
                <button
                  onClick={() => scrollTabs('left')}
                  className="absolute left-0 top-0 bottom-0 z-10 px-2 bg-gradient-to-r from-white via-white to-transparent flex items-center"
                >
                  <ChevronLeft className="h-5 w-5 text-gray-400" />
                </button>
              )}

              {/* Tabs container */}
              <div
                ref={tabsContainerRef}
                className="flex overflow-x-auto scrollbar-hide px-4"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                {toolEntries.map(([toolId, toolDef]) => {
                  const ToolIcon = ICON_MAP[toolDef.icon];
                  const isActive = activeTab === toolId;
                  return (
                    <button
                      key={toolId}
                      onClick={() => setActiveTab(toolId)}
                      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                        isActive
                          ? 'border-indigo-600 text-indigo-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {ToolIcon && <ToolIcon className="h-4 w-4" />}
                      {toolDef.name}
                    </button>
                  );
                })}
              </div>

              {/* Right scroll indicator */}
              {canScrollRight && (
                <button
                  onClick={() => scrollTabs('right')}
                  className="absolute right-0 top-0 bottom-0 z-10 px-2 bg-gradient-to-l from-white via-white to-transparent flex items-center"
                >
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </button>
              )}
            </div>
          </div>

          {/* Tab Content */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            {activeToolDef && (
              <Card>
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-3">
                    {ActiveToolIcon && (
                      <div className="p-2 bg-gray-100 rounded-lg">
                        <ActiveToolIcon className="h-5 w-5 text-gray-600" />
                      </div>
                    )}
                    <div>
                      <CardTitle>{activeToolDef.name}</CardTitle>
                      <CardDescription>{activeToolDef.description}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Alerts Table */}
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-muted/50">
                          <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground" rowSpan={2}>
                            Alert Type
                          </th>
                          <th colSpan={2} className="text-center py-2 px-2 font-semibold text-sm border-l bg-indigo-50 text-indigo-700">
                            Admin
                          </th>
                          <th colSpan={2} className="text-center py-2 px-2 font-semibold text-sm border-l bg-green-50 text-green-700">
                            Manager
                          </th>
                        </tr>
                        <tr className="bg-muted/30 border-t">
                          <th className="text-center py-2 px-3 font-medium text-xs text-muted-foreground border-l bg-indigo-50/50 w-20">
                            App
                          </th>
                          <th className="text-center py-2 px-3 font-medium text-xs text-muted-foreground bg-indigo-50/50 w-20">
                            Email
                          </th>
                          <th className="text-center py-2 px-3 font-medium text-xs text-muted-foreground border-l bg-green-50/50 w-20">
                            App
                          </th>
                          <th className="text-center py-2 px-3 font-medium text-xs text-muted-foreground bg-green-50/50 w-20">
                            Email
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {Object.entries(activeToolDef.alerts).map(([alertId, alertDef]) => {
                          const adminAlert = adminSettings[activeTab]?.[alertId] || { app: false, email: false };
                          const managerAlert = managerSettings[activeTab]?.[alertId] || { app: false, email: false };

                          return (
                            <tr key={alertId} className="hover:bg-muted/30">
                              <td className="py-3 px-4">
                                <div className="font-medium text-sm">{alertDef.label}</div>
                                <div className="text-xs text-muted-foreground mt-0.5">{alertDef.description}</div>
                              </td>
                              <td className="py-3 px-3 text-center border-l bg-indigo-50/20">
                                <div className="flex justify-center">
                                  <Checkbox
                                    checked={adminAlert.app}
                                    onCheckedChange={(v) => handleAlertChange('Admin', activeTab, alertId, 'app', v)}
                                  />
                                </div>
                              </td>
                              <td className="py-3 px-3 text-center bg-indigo-50/20">
                                <div className="flex justify-center">
                                  <Checkbox
                                    checked={adminAlert.email}
                                    onCheckedChange={(v) => handleAlertChange('Admin', activeTab, alertId, 'email', v)}
                                  />
                                </div>
                              </td>
                              <td className="py-3 px-3 text-center border-l bg-green-50/20">
                                <div className="flex justify-center">
                                  <Checkbox
                                    checked={managerAlert.app}
                                    onCheckedChange={(v) => handleAlertChange('Manager', activeTab, alertId, 'app', v)}
                                  />
                                </div>
                              </td>
                              <td className="py-3 px-3 text-center bg-green-50/20">
                                <div className="flex justify-center">
                                  <Checkbox
                                    checked={managerAlert.email}
                                    onCheckedChange={(v) => handleAlertChange('Manager', activeTab, alertId, 'email', v)}
                                  />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Thresholds for Check-ins */}
                  {showCheckinThresholds && (
                    <div className="mt-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="h-4 w-4 text-orange-600" />
                        <Label className="text-sm font-medium">Critical Shortage Threshold</Label>
                      </div>
                      <p className="text-xs text-muted-foreground mb-3">
                        Number of unmet requirements before marking as critical
                      </p>
                      <Input
                        type="number"
                        min="1"
                        max="20"
                        value={thresholdConfig.critical_shortage_threshold}
                        onChange={(e) => {
                          setThresholdConfig(p => ({ ...p, critical_shortage_threshold: parseInt(e.target.value) || 3 }));
                          setHasChanges(true);
                        }}
                        className="w-24"
                      />
                    </div>
                  )}

                  {/* Thresholds for Qualifications */}
                  {showQualificationThresholds && (
                    <div className="mt-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="h-4 w-4 text-purple-600" />
                        <Label className="text-sm font-medium">Expiry Warning Thresholds</Label>
                      </div>
                      <p className="text-xs text-muted-foreground mb-3">
                        Days before certificate expiry to send warning alerts
                      </p>

                      {/* Thresholds List */}
                      <div className="space-y-2 mb-4">
                        {thresholdConfig.expiry_warning_days.length === 0 ? (
                          <p className="text-sm text-muted-foreground italic">No thresholds configured</p>
                        ) : (
                          thresholdConfig.expiry_warning_days.map((days) => (
                            <div
                              key={days}
                              className="flex items-center justify-between bg-white border rounded-md px-3 py-2"
                            >
                              <div className="flex items-center gap-2">
                                <Clock className="h-4 w-4 text-purple-500" />
                                <span className="text-sm font-medium">{days} days before expiry</span>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveExpiryThreshold(days)}
                                className="h-7 w-7 p-0 text-gray-400 hover:text-red-600"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ))
                        )}
                      </div>

                      {/* Add New Threshold */}
                      <div className="flex gap-2">
                        <Input
                          type="number"
                          min="1"
                          max="365"
                          placeholder="Days"
                          value={newThresholdDays}
                          onChange={(e) => setNewThresholdDays(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddExpiryThreshold();
                            }
                          }}
                          className="w-24"
                        />
                        <Button variant="outline" size="sm" onClick={handleAddExpiryThreshold}>
                          <Plus className="h-4 w-4 mr-1" />
                          Add Threshold
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </PageTransition>
    </AppShell3>
  );
}
